// runMapping.ts
// ---------------------------------------------------------------------------
// Pure transformer: backend `Run` row → UI view-model (`RunVM`).
//
// The Kanban prototype was designed against an idealized run shape with
// fields like `column`, `mode`, `agent`, `reviewer`, `zone`, `loop`, `cost`,
// `pr`, `lastEvent`, `sandbox`, etc. The actual platform Run row (see
// packages/run-store/src/types.ts) is leaner — `status`, `pr_number`, `branch`,
// `strategy`, `cost_usd`, `error`, plus a freeform `metadata` JSONB column.
//
// Mapping rules:
//
//   column derivation:
//     status === 'queued'                                   → 'backlog'
//     status === 'running'  + pr_number is set              → 'review'
//     status === 'running'  otherwise                       → 'implementing'
//     status === 'awaiting_review'                          → 'pr'
//     status === 'succeeded' + pr_number set + not merged   → 'pr'
//     status === 'succeeded' + metadata.merged              → 'done' (merged)
//     status === 'failed'/'escalated'/'cancelled'           → 'done' (escalated)
//
//   doneState derivation:
//     'succeeded' with metadata.merged === true             → 'merged'
//     anything else terminal in 'done'                      → 'escalated'
//
//   source: from metadata.source ('jira' | 'chat' | 'manual'); falls back to
//     'jira' if jira_issue_key is set, otherwise 'manual'.
//
//   mode: from metadata.execution_mode ('autonomous'|'assisted'). Defaults to
//     'autonomous' (the platform default).
//
//   agent / reviewer: from metadata.agent_provider / metadata.review_provider.
//     Defaults to '?' so the UI never crashes on missing telemetry.
//
//   zone: from metadata.zone — orchestrator may stamp 'red'|'green'|'mixed'
//     when it classifies. Null when unknown.
//
//   loop: from metadata.loop = { current, max, issues, critical? }. The
//     orchestrator is expected to upsert these per-loop iteration; if absent,
//     we synthesize a stub with max from metadata.review_loops or 0.
//
//   cost: parseFloat of cost_usd (string-typed for precision, accept loss for
//     display). Falls back to 0.
//
//   pr: { number: pr_number, additions/deletions/label } — additions/deletions
//     come from metadata.pr_additions / metadata.pr_deletions if the
//     orchestrator stamped them; label from metadata.pr_label or derived from
//     audit_verdict (block → needs-human-intervention; pass/null → human-ready).
//
//   lastEvent: NOT derived in this transformer. The orchestrator-side row
//     doesn't carry the most recent event — that lives in a separate `events`
//     table. Callers that want lastEvent must merge it in (live event stream
//     or first row from /runs/:id recent_events).
//
// All caller-side derivations should funnel through this file, including
// tests. Keep it pure: no I/O, no React, no module-level state.

import type {
  ApiRun,
  ColumnId,
  DoneState,
  Mode,
  RunVM,
  Zone,
  AcceptanceCriterion,
  RunPR,
  LastEvent,
  ApiEventLine,
} from './types';

interface OrchestratorMetadata {
  source?: 'jira' | 'chat' | 'manual';
  execution_mode?: Mode;
  agent_provider?: string;
  review_provider?: string;
  zone?: Zone;
  loop?: { current: number; max: number; issues: number; critical?: boolean };
  review_loops?: number;
  pr_title?: string;
  pr_additions?: number;
  pr_deletions?: number;
  pr_label?: 'human-ready' | 'needs-human-intervention' | 'merged' | 'awaiting-review';
  pr_reason?: string;
  merged?: boolean;
  closed?: boolean;
  /** Operator clicked "dismiss" on a terminal-but-in-pr-column run (e.g. a
   *  succeeded webhook-fired run that wasn't agent-generated and shouldn't
   *  clutter the queue). Status stays succeeded; UI moves card to 'done'. */
  dismissed_at?: string;
  awaiting_review_since?: string;
  keepalive_until?: string;
  keepalive_minutes?: number;
  escalation_reason?: string;
  workspace_path?: string;
  sandbox_id?: string;
  sandbox_region?: string;
  cancellation_requested?: boolean;
  blocked_reason?: string;
  description?: string;
  title?: string;
  acceptance_criteria?: Array<string | AcceptanceCriterion>;
  ac?: Array<string | AcceptanceCriterion>;
  phase?: RunVM['phase'];
  progress?: number;
  warning?: 'missing-ac';
  // M13: surfaces session-id and target-branch info on the runs row.
  // session_id is stamped via the orchestrator's onSession hook the
  // moment the strategy creates a session; target_branch{,_url} is
  // stamped at run start (predictable from owner/repo + ticket id).
  session_id?: string;
  session_strategy?: string;
  target_branch?: string;
  target_branch_url?: string;
}

function meta(run: ApiRun): OrchestratorMetadata {
  return (run.metadata ?? {}) as OrchestratorMetadata;
}

/**
 * Hide runs the kanban shouldn't surface. Today the only rule: a run created
 * by the GitHub webhook in response to `pull_request.opened` on an
 * agent-generated PR is a duplicate of the ticket-driven run that actually
 * did the work (the ticket's own orchestrator opens the PR + runs its own
 * review loop). The webhook-fired run shows up as `ticket_id='pr-NN'` with
 * `metadata.trigger='pr'` + `metadata.source='github-webhook'` — a zombie
 * card with no meaningful identity.
 *
 * Filter intent (per the operator): only show runs that started via an
 * explicit entry point — Jira, UI, API, CLI, or /runs/from-pr. Webhook-
 * initiated PR runs are noise.
 *
 * Manual operator dispatches via POST /runs/from-pr ARE shown because their
 * source is 'manual-pr-dispatch', not 'github-webhook'.
 */
export function isHiddenFromKanban(run: ApiRun): boolean {
  const m = (run.metadata ?? {}) as { trigger?: unknown; source?: unknown };
  return m.trigger === 'pr' && m.source === 'github-webhook';
}

export function deriveColumn(run: ApiRun): ColumnId {
  const m = meta(run);
  switch (run.status) {
    case 'draft':
      // Parked drafts share the Backlog column with queued runs; the card
      // renderer distinguishes them by status (a 'draft' badge + a queue
      // button replaces the running indicator).
      return 'backlog';
    case 'queued':
      return 'backlog';
    case 'running':
      // If PR has been created the agent is in review territory.
      return run.pr_number != null ? 'review' : 'implementing';
    case 'awaiting_review':
      // Internal loop done, PR open, waiting on external feedback / merge.
      // Merge / close webhooks stamp metadata before the status transition
      // lands (EXTERNAL-FEEDBACK component #10) so the card moves to 'done'
      // as soon as the metadata signals the PR's fate.
      if (m.merged === true) return 'done';
      if (m.closed === true) return 'done';
      return 'pr';
    case 'succeeded':
      if (m.merged === true) return 'done';
      // A PR closed without merge — the pull_request.closed webhook
      // stamped `closed: true` on the run's metadata. The run can't
      // transition out of `succeeded` (terminal), but the card should
      // leave the human queue since the PR is no longer actionable.
      // Treat it the same as merged for column placement.
      if (m.closed === true) return 'done';
      // Dismissed-from-pr-column runs are terminal but should not clutter
      // the human queue. Stamping `dismissed_at` is the operator's "I'm
      // done with this card, get it off the board" signal. The row stays
      // succeeded for audit; the card just moves to 'done'.
      if (m.dismissed_at) return 'done';
      if (run.pr_number != null) return 'pr';
      // succeeded with no PR is unusual but possible — surface in done.
      return 'done';
    case 'failed':
    case 'escalated':
    case 'cancelled':
      return 'done';
  }
}

export function deriveDoneState(run: ApiRun): DoneState | undefined {
  if (deriveColumn(run) !== 'done') return undefined;
  const m = meta(run);
  // `merged` is the only positive done-state. It can come from either
  // `succeeded + metadata.merged` (today's path) or `awaiting_review +
  // metadata.merged` (post EXTERNAL-FEEDBACK component #4, when the
  // orchestrator parks runs in awaiting_review until the PR closes).
  if (m.merged) return 'merged';
  return 'escalated';
}

export function deriveSource(run: ApiRun): RunVM['source'] {
  const m = meta(run);
  if (m.source === 'jira' || m.source === 'chat' || m.source === 'manual') {
    return m.source;
  }
  return run.jira_issue_key ? 'jira' : 'manual';
}

export function deriveMode(run: ApiRun): Mode {
  const m = meta(run);
  return m.execution_mode === 'assisted' ? 'assisted' : 'autonomous';
}

export function deriveZone(run: ApiRun): Zone | null {
  const m = meta(run);
  if (m.zone === 'red' || m.zone === 'green' || m.zone === 'mixed') return m.zone;
  return null;
}

export function deriveLoop(run: ApiRun): RunVM['loop'] {
  const m = meta(run);
  if (m.loop && typeof m.loop.current === 'number') {
    return {
      current: m.loop.current,
      max: m.loop.max,
      issues: m.loop.issues,
      ...(m.loop.critical ? { critical: true } : {}),
    };
  }
  const max = typeof m.review_loops === 'number' ? m.review_loops : 0;
  return { current: 0, max, issues: 0 };
}

export function deriveCost(run: ApiRun): number {
  if (run.cost_usd == null) return 0;
  const n = parseFloat(run.cost_usd);
  return Number.isFinite(n) ? n : 0;
}

export function derivePR(run: ApiRun): RunPR | undefined {
  if (run.pr_number == null) return undefined;
  const m = meta(run);
  let label: RunPR['label'];
  if (m.pr_label) {
    label = m.pr_label;
  } else if (m.merged) {
    label = 'merged';
  } else if (run.audit_verdict === 'block' || run.status === 'escalated') {
    label = 'needs-human-intervention';
  } else if (run.status === 'awaiting_review') {
    // Parked between internal review approval and the PR's actual fate
    // (merge / close). The orchestrator finished cleanly; the operator is
    // waiting on external review feedback or a merge.
    label = 'awaiting-review';
  } else {
    label = 'human-ready';
  }
  return {
    number: run.pr_number,
    additions: m.pr_additions ?? 0,
    deletions: m.pr_deletions ?? 0,
    label,
    ...(m.pr_reason ? { reason: m.pr_reason } : {}),
  };
}

export function deriveAcceptanceCriteria(run: ApiRun): AcceptanceCriterion[] {
  const m = meta(run);
  const raw = m.acceptance_criteria ?? m.ac ?? [];
  return raw.map((entry): AcceptanceCriterion => {
    if (typeof entry === 'string') return { text: entry, done: false };
    return {
      text: entry.text ?? '',
      done: !!entry.done,
    };
  });
}

export function derivePhase(run: ApiRun): RunVM['phase'] {
  const m = meta(run);
  if (m.phase) return m.phase;
  if (run.status === 'running') {
    return run.pr_number != null ? 'review' : 'implementing';
  }
  return null;
}

export function deriveBlocked(run: ApiRun): string | null {
  const m = meta(run);
  if (m.blocked_reason) return m.blocked_reason;
  if (m.agent_provider && m.review_provider && m.agent_provider === m.review_provider) {
    return 'reviewer-eq-agent';
  }
  return null;
}

export function deriveLocal(run: ApiRun): boolean {
  // Strategies that run in-process against a local repo are 'claude-agent-sdk',
  // 'codex-sdk' (against a local Codex CLI), 'pi-agent'. Sandboxed strategies
  // are 'claude-managed' and 'pi-managed'. Use strategy as the local/sandbox
  // signal — it's the authoritative source per CLAUDE.md.
  const localStrategies = new Set(['claude-agent-sdk', 'codex-sdk', 'pi-agent']);
  return run.strategy ? localStrategies.has(run.strategy) : false;
}

export function deriveProgress(run: ApiRun): number | null {
  const m = meta(run);
  if (typeof m.progress === 'number') return m.progress;
  // Coarse fallback so the UI bar never reads as zero on a live run.
  if (run.status === 'running') return 0.05;
  return null;
}

/**
 * Map a single backend Run row to the UI view-model. Pure — no I/O.
 *
 * `lastEvent` is left null; callers should overlay live or recent events
 * after calling this. See useRuns / useRunStream for the wiring.
 */
export function runToVM(run: ApiRun): RunVM {
  const m = meta(run);
  return {
    id: run.ticket_id,
    runId: run.id,
    column: deriveColumn(run),
    doneState: deriveDoneState(run),
    // Webhook-spawned PR runs end up with ticket_id="pr-17" and no ticket
    // title — so the old fallback chain `m.title ?? run.ticket_id` painted
    // the card title as "pr-17" right under the "[pr-17]" header. Prefer
    // the real GitHub PR title (stamped by the orchestrator after getPR)
    // so the card communicates what the PR is, not just its number.
    title: m.pr_title ?? m.title ?? run.ticket_id,
    source: deriveSource(run),
    mode: deriveMode(run),
    local: deriveLocal(run),
    agent: m.agent_provider ?? '?',
    reviewer: m.review_provider ?? '?',
    zone: deriveZone(run),
    phase: derivePhase(run),
    progress: deriveProgress(run),
    loop: deriveLoop(run),
    cost: deriveCost(run),
    ...(derivePR(run) ? { pr: derivePR(run)! } : {}),
    sandbox: m.sandbox_id ?? null,
    sandboxRegion: m.sandbox_region ?? null,
    description: m.description ?? '',
    ac: deriveAcceptanceCriteria(run),
    blocked: deriveBlocked(run),
    ...(m.warning ? { warning: m.warning } : {}),
    ...(isHiddenFromKanban(run) ? { hiddenFromKanban: true } : {}),
    lastEvent: null,
    branch: run.branch,
    strategy: run.strategy,
    status: run.status,
    startedAt: run.started_at,
    finishedAt: run.finished_at,
    // M13: session + target-branch surfacing
    sessionId: m.session_id ?? null,
    sessionStrategy: m.session_strategy ?? null,
    targetBranch: m.target_branch ?? null,
    targetBranchUrl: m.target_branch_url ?? null,
    // RLEF multi-repo. Passed through so the drawer / cards can build
    // accurate per-repo URLs without relying on the platform-default env.
    githubOwner: run.github_owner ?? null,
    githubRepo: run.github_repo ?? null,
  };
}

/**
 * Convert a wire event line into the compact LastEvent view-model used on the
 * card footer. Heuristics:
 *
 * - `event` field maps to `type` (with normalization to a small set the CSS
 *   knows about — tool_use, thinking, review_feedback, pr_created, merged,
 *   escalated, cancelled, system, run_start, phase_start).
 * - `text` is the most descriptive payload field we can find.
 */
export function eventToLast(line: ApiEventLine): LastEvent {
  const eventName = String(line.event ?? 'event');
  const type = normalizeEventType(eventName);
  const text = pickEventText(eventName, line);
  return { type, text };
}

const KNOWN_TYPE_MAP: Record<string, string> = {
  // strategy lifecycle
  claude_agent_sdk_start: 'run_start',
  claude_agent_sdk_turn: 'tool_use',
  claude_agent_sdk_complete: 'phase_start',
  claude_managed_start: 'run_start',
  claude_managed_session_created: 'phase_start',
  claude_managed_resumed: 'phase_start',
  claude_managed_complete: 'phase_start',
  codex_sdk_start: 'run_start',
  codex_thread_started: 'phase_start',
  codex_command: 'tool_use',
  codex_file_change: 'tool_use',
  codex_mcp_call: 'tool_use',
  codex_web_search: 'tool_use',
  codex_sdk_complete: 'phase_start',
  pi_agent_start: 'run_start',
  pi_agent_turn: 'tool_use',
  pi_agent_complete: 'phase_start',
  pi_managed_start: 'run_start',
  pi_managed_image_resolved: 'phase_start',
  pi_managed_machine_created: 'phase_start',
  pi_managed_machine_started: 'phase_start',
  pi_managed_sidecar_ready: 'phase_start',
  pi_managed_runner_starting: 'phase_start',
  pi_managed_clone_ok: 'phase_start',
  pi_managed_turn: 'tool_use',
  // Mid-turn visibility — emitted when a tool starts/ends inside a turn so
  // the chat UI doesn't go silent for minutes during long bash (npm install
  // etc.). 'tool_use' kind groups them with the turn bubbles.
  pi_managed_tool_start: 'tool_use',
  pi_managed_tool_end: 'tool_use',
  pi_managed_push: 'pr_created',
  pi_managed_runner_complete: 'phase_start',
  pi_managed_complete: 'phase_start',
  pi_managed_aborted: 'cancelled',
  pi_managed_run_failed: 'system',
  pi_managed_resume_unsupported: 'system',
  // orchestrator steps
  run_fix_via_strategy_start: 'phase_start',
  run_fix_via_strategy_complete: 'phase_start',
  run_fix_via_strategy_error: 'system',
  run_fix_workspace_ready: 'phase_start',
  run_fix_workspace_failed: 'system',
  run_fix_push_failed: 'system',
  run_fix_collect_edits_failed: 'system',
  run_fix_branch_confirm_missing: 'system',
  run_fix_branch_confirm_failed: 'system',
  audit_report: 'review_feedback',
  // internal review loop (drives the Reviews tab in Drawer.tsx)
  review_completed: 'review_feedback',
  pr_approved: 'merged',
  review_escalated: 'escalated',
  run_fix_completed: 'phase_start',
  loop_started: 'phase_start',
  loop_aborted: 'cancelled',
  // multi-tenant attach (operator teleports into the pi-managed sandbox).
  // 'system' bucket — these don't drive the column / phase visuals, but the
  // run drawer timeline should surface them as one-line audit entries.
  run_attached: 'system',
  // operator-queued user interjection for the live pi-runner (POST
  // /runs/:id/interject). Same audit-only intent as run_attached.
  run_interjection: 'system',
  // streaming-side meta
  run_started: 'run_start',
  // `run_complete` is the orchestrator's "run reached terminal state" event.
  // It fires regardless of outcome — succeeded, failed, escalated, or
  // cancelled. Past versions mapped it to the celebratory `merged` type,
  // which painted every failed run's card-footer LastEvent badge purple
  // with the literal label "merged" — directly contradicting the run-level
  // doneState badge on the card head. Map to the neutral `phase_start`
  // type so the LastEvent reflects "run finished" without claiming an
  // outcome; the doneState badge owns the success/fail visual.
  run_complete: 'phase_start',
  tail_error: 'system',
};

function normalizeEventType(eventName: string): string {
  if (KNOWN_TYPE_MAP[eventName]) return KNOWN_TYPE_MAP[eventName];
  // Common bare names already match our CSS classes; pass through.
  if (
    eventName === 'tool_use' ||
    eventName === 'thinking' ||
    eventName === 'review_feedback' ||
    eventName === 'pr_created' ||
    eventName === 'merged' ||
    eventName === 'escalated' ||
    eventName === 'cancelled' ||
    eventName === 'system' ||
    eventName === 'run_start' ||
    eventName === 'phase_start'
  ) {
    return eventName;
  }
  return 'system';
}

function pickEventText(eventName: string, line: ApiEventLine): string {
  // pi-managed lifecycle events carry structured fields (machine_id, image,
  // branch, error, …) but no human-readable `text`. Synthesize one so the
  // events tab reads as a timeline instead of opaque names.
  if (eventName.startsWith('pi_managed_')) {
    const pretty = prettyPiManagedText(eventName, line);
    if (pretty !== null) return pretty;
  }
  if (eventName === 'run_attached') {
    const pretty = prettyRunAttachedText(line);
    if (pretty !== null) return pretty;
  }
  if (eventName === 'run_interjection') {
    const pretty = prettyRunInterjectionText(line);
    if (pretty !== null) return pretty;
  }

  // Prefer well-known message-ish fields; fall back to a short summary line.
  // text_preview catches pi_agent_turn / claude_agent_sdk_turn narration that
  // pickEventText doesn't otherwise know how to format.
  const candidates = [
    'text',
    'text_preview',
    'message',
    'title',
    'tool',
    'tool_name',
    'command',
    'path',
    'file',
    'summary',
    'reason',
    'error',
  ];
  for (const field of candidates) {
    const val = line[field];
    if (typeof val === 'string' && val.length > 0) return val;
  }
  return eventName;
}

function strField(line: ApiEventLine, key: string): string | null {
  const v = line[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function numField(line: ApiEventLine, key: string): number | null {
  const v = line[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function arrField(line: ApiEventLine, key: string): string[] | null {
  const v = line[key];
  if (!Array.isArray(v)) return null;
  const out = v.filter((s): s is string => typeof s === 'string' && s.length > 0);
  return out.length > 0 ? out : null;
}

// Drop the `agent/` prefix and ticket id grunge so a branch reads compactly.
function shortBranch(branch: string | null): string | null {
  if (!branch) return null;
  return branch.replace(/^agent\//, '');
}

// Image refs look like registry.fly.io/agentic-compute:deployment-01KR... — the
// tag is the only part operators care about at a glance.
function shortImage(image: string | null): string | null {
  if (!image) return null;
  const tagIdx = image.lastIndexOf(':');
  return tagIdx >= 0 ? image.slice(tagIdx + 1) : image;
}

// Fly Machine ids are 14 hex chars; the first 8 disambiguates fine in a UI row.
function shortMachine(id: string | null): string | null {
  if (!id) return null;
  return id.length > 8 ? id.slice(0, 8) : id;
}

function prettyRunAttachedText(line: ApiEventLine): string | null {
  // Field shape comes from services/review-loop/src/index.ts → recordAttachEvent:
  //   { result, tenant_id, by, ip, machine_id?, token_id?, token_ttl_seconds?,
  //     status?, reason?, kind? }
  const result = strField(line, 'result');
  const by = strField(line, 'by');
  const reason = strField(line, 'reason');
  switch (result) {
    case 'attached': {
      const ttl = numField(line, 'token_ttl_seconds');
      const parts: string[] = [];
      parts.push(`attached by ${by ?? 'unknown'}`);
      if (ttl !== null) parts.push(`token ttl ${ttl}s`);
      return parts.join(' · ');
    }
    case 'denied': {
      const status = numField(line, 'status');
      const who = by ?? 'unknown';
      const why = reason ?? 'denied';
      return status === 404
        ? `denied (404 — not yours): ${who}`
        : `denied (${status ?? '?'}): ${who} — ${why}`;
    }
    case 'error': {
      const kind = strField(line, 'kind');
      return kind ? `attach error: ${kind}` : (reason ?? 'attach error');
    }
    case 'revoked': {
      const tok = strField(line, 'token_id');
      return tok ? `token revoked by ${by ?? 'unknown'} (${tok.slice(0, 8)})` : `token revoked by ${by ?? 'unknown'}`;
    }
    case 'revoke_failed':
      return reason ? `revoke failed: ${reason}` : 'revoke failed';
    default:
      return null;
  }
}

function prettyRunInterjectionText(line: ApiEventLine): string | null {
  // Field shape comes from services/review-loop/src/index.ts → run_interjection:
  //   { result, by, tenant_id, ip, machine_id, bytes, message_id?, kind?, reason? }
  const result = strField(line, 'result');
  const by = strField(line, 'by') ?? 'unknown';
  const bytes = numField(line, 'bytes');
  if (result === 'queued') {
    return bytes !== null
      ? `queued by ${by} (${bytes} bytes)`
      : `queued by ${by}`;
  }
  if (result === 'failed') {
    const kind = strField(line, 'kind');
    const reason = strField(line, 'reason');
    if (kind === 'no_runner') return `queue failed — no active runner (by ${by})`;
    if (kind === 'machine_gone') return `queue failed — machine gone (by ${by})`;
    if (reason) return `queue failed — ${reason}`;
    return `queue failed (by ${by})`;
  }
  return null;
}

function prettyPiManagedText(eventName: string, line: ApiEventLine): string | null {
  switch (eventName) {
    case 'pi_managed_start': {
      const model = strField(line, 'model');
      const branch = shortBranch(strField(line, 'branch'));
      const region = strField(line, 'region');
      const parts: string[] = [];
      if (model) parts.push(model);
      if (branch) parts.push(`→ ${branch}`);
      if (region) parts.push(`@${region}`);
      return parts.length > 0 ? parts.join(' ') : null;
    }
    case 'pi_managed_image_resolved': {
      const tag = shortImage(strField(line, 'image'));
      const source = strField(line, 'source');
      if (tag && source) return `${tag} (${source})`;
      return tag ?? null;
    }
    case 'pi_managed_machine_created': {
      const id = shortMachine(strField(line, 'machine_id'));
      const region = strField(line, 'region');
      if (id && region) return `machine ${id} @${region}`;
      return id ? `machine ${id}` : null;
    }
    case 'pi_managed_machine_started': {
      const id = shortMachine(strField(line, 'machine_id'));
      return id ? `machine ${id} ready` : 'machine ready';
    }
    case 'pi_managed_sidecar_ready': {
      const id = shortMachine(strField(line, 'machine_id'));
      return id ? `sidecar /health ok (${id})` : 'sidecar /health ok';
    }
    case 'pi_managed_runner_starting': {
      const model = strField(line, 'model');
      const branch = shortBranch(strField(line, 'branch'));
      if (model && branch) return `runner: ${model} on ${branch}`;
      return model ? `runner: ${model}` : 'runner starting';
    }
    case 'pi_managed_clone_ok': {
      const repo = strField(line, 'repo') || strField(line, 'repo_url');
      return repo ? `cloned ${repo}` : 'cloned';
    }
    case 'pi_managed_runner_complete': {
      const turns = numField(line, 'num_turns');
      const cost = numField(line, 'cost_usd');
      const parts: string[] = ['runner done'];
      if (turns !== null) parts.push(`${turns} turns`);
      if (cost !== null) parts.push(`$${cost.toFixed(4)}`);
      return parts.join(' · ');
    }
    case 'pi_managed_push': {
      const ok = line['ok'];
      const branch = shortBranch(strField(line, 'branch'));
      if (ok === false) {
        const err = strField(line, 'error');
        return err ? `push failed: ${err}` : 'push failed';
      }
      const sha = strField(line, 'sha');
      const noChanges = line['no_changes'] === true;
      if (noChanges) return branch ? `no changes on ${branch}` : 'no changes';
      const shortSha = sha ? sha.slice(0, 7) : null;
      if (branch && shortSha) return `pushed ${branch}@${shortSha}`;
      return branch ? `pushed ${branch}` : 'pushed';
    }
    case 'pi_managed_complete': {
      const stop = strField(line, 'stop_reason');
      const turns = numField(line, 'num_turns');
      const branchPushed = line['branch_pushed'];
      const parts: string[] = [];
      if (stop) parts.push(stop);
      if (turns !== null) parts.push(`${turns} turns`);
      if (branchPushed === true) parts.push('pushed');
      return parts.length > 0 ? parts.join(' · ') : null;
    }
    case 'pi_managed_run_failed': {
      const err = strField(line, 'error');
      const id = shortMachine(strField(line, 'machine_id'));
      if (err && id) return `✗ ${err} (machine ${id})`;
      return err ? `✗ ${err}` : '✗ run failed';
    }
    case 'pi_managed_aborted': {
      const reason = strField(line, 'reason') || strField(line, 'error');
      return reason ? `aborted: ${reason}` : 'aborted';
    }
    case 'pi_managed_resume_unsupported': {
      const reason = strField(line, 'reason');
      return reason ? `resume unsupported: ${reason}` : 'resume unsupported';
    }
    case 'pi_managed_turn': {
      // Match the emit shape from strategies/pi-managed.ts: tools[] + text_preview.
      // Older field names (tool, text) are also accepted as a fallback so
      // historical events still render.
      const tools = arrField(line, 'tools');
      const tool = tools && tools.length > 0
        ? (tools.length === 1 ? tools[0] : tools.join('+'))
        : strField(line, 'tool') || strField(line, 'tool_name');
      const text = strField(line, 'text_preview') || strField(line, 'text');
      if (tool && text) return `${tool}: ${text}`;
      return tool ?? text ?? null;
    }
    default:
      return null;
  }
}
